import {
  SubscriptionSearchParams,
  SubscriptionListResponse,
  SubscriptionRecord,
  SubscriptionType,
  MemberLevel,
  OrderStatus,
  PaymentChannel,
  Platform,
} from './types';
import { getAppleSubscribeQuery, getGoogleSubscribeQuery } from '@/api/order';
import { AppleSubscribeRecord, GoogleSubscribeRecord, GoogleSubscribeQueryParams } from '@/types/order';
import dayjs from 'dayjs';

/**
 * 将时间戳数字转为毫秒（dayjs 需要毫秒）
 */
const toMs = (value: number): number => {
  if (value > 1e13) return Math.floor(value / 1000); // 误乘过 1000 的毫秒，还原
  if (value > 1e12) return value; // 已是毫秒
  return value * 1000; // 秒 -> 毫秒
};

/**
 * 将时间对象或字符串转换为字符串格式
 */
const formatTimeToString = (time: Record<string, unknown> | string | number | null | undefined): string => {
  if (time == null) return '';
  if (typeof time === 'number') {
    const ms = toMs(time);
    const d = dayjs(ms);
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : '';
  }
  // 如果是字符串，直接返回
  if (typeof time === 'string') {
    // 如果是 ISO 格式或时间戳字符串，转换为标准格式
    const parsed = dayjs(time);
    if (parsed.isValid()) {
      return parsed.format('YYYY-MM-DD HH:mm:ss');
    }
    return time;
  }
  
  // 如果是对象，尝试提取时间信息
  if (typeof time === 'object' && time !== null) {
    try {
      // 尝试从对象中提取时间戳或日期字段（统一按毫秒传给 dayjs）
      if ('timestamp' in time && typeof time.timestamp === 'number') {
        return dayjs(toMs(time.timestamp)).format('YYYY-MM-DD HH:mm:ss');
      }
      if ('seconds' in time && typeof time.seconds === 'number') {
        return dayjs(time.seconds * 1000).format('YYYY-MM-DD HH:mm:ss');
      }
      if ('_seconds' in time && typeof time._seconds === 'number') {
        return dayjs(time._seconds * 1000).format('YYYY-MM-DD HH:mm:ss');
      }
      // 尝试从对象中提取日期字符串
      if ('date' in time && typeof time.date === 'string') {
        const parsed = dayjs(time.date);
        if (parsed.isValid()) {
          return parsed.format('YYYY-MM-DD HH:mm:ss');
        }
      }
      // 如果对象有 toString 方法，尝试使用
      if (typeof time.toString === 'function') {
        const dateStr = time.toString();
        const parsed = dayjs(dateStr);
        if (parsed.isValid()) {
          return parsed.format('YYYY-MM-DD HH:mm:ss');
        }
      }
    } catch (e: unknown) {
      console.warn('Failed to parse time object:', time, e);
    }
    return '';
  }
  
  return '';
};

/**
 * 将 AppleSubscribeRecord 转换为 SubscriptionRecord
 */
const convertAppleRecordToSubscriptionRecord = (
  record: AppleSubscribeRecord
): SubscriptionRecord => {
  // 映射 periodType（兜底用，优先按 productId 后缀判断）：
  // 0-monthly 月付；1-半年付（苹果 V1 _6monthly 历史 SKU）
  const periodTypeMap: Record<number, SubscriptionType> = {
    0: SubscriptionType.MONTHLY,
    1: SubscriptionType.ANNUAL,
    3: SubscriptionType.QUARTERLY,
  };

  // 映射 level: 0=Pro，1=Premium，2=Android 显示 Basic / iOS 显示 Lite
  const levelMap: Record<number, MemberLevel | number> = {
    0: MemberLevel.ADVANCED, // Pro
    1: MemberLevel.PREMIUM, // Premium
    2: 2, // 列表根据 paymentChannel 显示 Basic 或 Lite
  };

  // 会员状态：1=有效，2=过期，3=撤销/退款
  const statusMap: Record<number, OrderStatus> = {
    1: OrderStatus.ACTIVE,
    2: OrderStatus.EXPIRED,
    3: OrderStatus.CANCELLED,
  };

  const transactionId = record.transactionId || '-';
  const startTime = formatTimeToString(record.startTime);
  const expireTime = formatTimeToString(record.expireTime);
  const registerTime = record.registerTime ? formatTimeToString(record.registerTime) : undefined;

  // 订阅周期：优先 productId 后缀（覆盖苹果 12 个有效 SKU 的 _monthly/_6monthly/_yearly），
  // 后端 periodType 字段作兜底
  const applePid = record.productId ?? '';
  const subscriptionType = applePid.endsWith('_yearly')
    ? SubscriptionType.YEARLY
    : applePid.endsWith('_6monthly') || applePid.endsWith('_halfyear')
      ? SubscriptionType.ANNUAL
      : applePid.endsWith('_quarterly')
        ? SubscriptionType.QUARTERLY
        : applePid.endsWith('_monthly')
          ? SubscriptionType.MONTHLY
          : (periodTypeMap[record.periodType] ?? SubscriptionType.MONTHLY);

  return {
    transactionId,
    subscriptionStartTime: startTime,
    subscriptionEndTime: expireTime,
    paymentAmount: record.amount ?? 0,
    currency: String(record.currency ?? ''),
    suppId: String(record.suppId ?? ''),
    username: record.username || '',
    sourceType: record.sourceType,
    registerTime,
    subscriptionType,
    memberLevel: levelMap[record.level] ?? MemberLevel.ADVANCED,
    productId: record.productId,
    notificationType:
      record.notificationType == null || record.notificationType === ''
        ? undefined
        : Number.isFinite(Number(record.notificationType))
          ? Number(record.notificationType)
          : record.notificationType,
    orderStatus: statusMap[record.status] ?? OrderStatus.ACTIVE,
    remainingDays: record.remainingDays,
    renewalStatus: record.renewalStatus ?? 0,
    paymentChannel: PaymentChannel.APP_STORE,
    afSource: record.afSource,
    afCampaign: record.afCampaign,
    deviceType: record.deviceType,
  };
};

/**
 * 将 GoogleSubscribeRecord 转换为 SubscriptionRecord
 */
const convertGoogleRecordToSubscriptionRecord = (
  record: GoogleSubscribeRecord
): SubscriptionRecord => {
  // 会员状态：1=有效，2=过期，3=撤销/退款（与苹果一致）
  const statusMap: Record<number, OrderStatus> = {
    1: OrderStatus.ACTIVE,
    2: OrderStatus.EXPIRED,
    3: OrderStatus.CANCELLED,
  };

  const startTime = formatTimeToString(record.startTime);
  const expireTime = formatTimeToString(record.expireTime);
  const registerTime = record.registerTime ? formatTimeToString(record.registerTime) : undefined;
  const remainingDays = record.remainingDays ?? 0;
  const orderStatus = statusMap[record.status] ?? OrderStatus.ACTIVE;

  // 按 productId 后缀推断订阅周期：_yearly → YEARLY 年付（安卓有效）；
  // _6monthly → ANNUAL 半年付（防御历史数据，安卓当前无半年付 SKU）；其余按月付
  const productIdStr = record.productId ?? '';
  const subscriptionType = productIdStr.endsWith('_yearly')
    ? SubscriptionType.YEARLY
    : productIdStr.endsWith('_6monthly')
      ? SubscriptionType.ANNUAL
      : SubscriptionType.MONTHLY;

  // 安卓：level 0/1/2 与苹果一致；无 level 时按 2，列表显示 Basic（paymentChannel 为 GOOGLE_PAY）
  const rawLevel = (record as { level?: number }).level;
  const memberLevel: MemberLevel | number =
    rawLevel === 2 ? 2
    : rawLevel === 1 ? MemberLevel.PREMIUM
    : rawLevel === 0 ? MemberLevel.ADVANCED
    : 2;


  return {
    transactionId: record.orderId || '-',
    subscriptionStartTime: startTime,
    subscriptionEndTime: expireTime,
    paymentAmount: record.amount ?? 0,
    currency: String(record.currency ?? ''),
    suppId: record.suppId ?? '',
    username: record.userName || '',
    sourceType: record.sourceType ?? 0,
    registerTime,
    subscriptionType,
    memberLevel,
    productId: record.productId,
    orderStatus,
    remainingDays,
    renewalStatus: record.autoRenewing ?? 0, // autoRenewing 映射到 renewalStatus
    paymentChannel: PaymentChannel.GOOGLE_PAY,
    notificationType:
      record.notificationType == null || record.notificationType === ''
        ? undefined
        : Number.isFinite(Number(record.notificationType))
          ? Number(record.notificationType)
          : record.notificationType,
    afSource: record.afSource,
    afCampaign: record.afCampaign,
    deviceType: record.deviceType,
  };
};

/**
 * 获取订阅列表（根据平台调用不同接口）
 */
export const getSubscriptionList = async (
  params: SubscriptionSearchParams
): Promise<SubscriptionListResponse> => {
  // 如果是苹果平台，调用苹果订阅查询接口（订阅生效时间：开始 00:00:00，结束 23:59:59；后端按秒级时间戳且会 *1000 转 ms，故这里传秒）
  if (params.platform === Platform.APPLE) {
    const appleParams = {
      suppId: params.suppId ? Number(params.suppId) : undefined,
      transactionId: params.transactionId,
      status: params.orderStatus, // 直接使用，因为 OrderStatus 已经是 1/2/3
      renewalStatus: params.renewalStatus,
      // 苹果通知类型是字符串枚举（DID_RENEW / SUBSCRIBED 等）
      notificationType:
        typeof params.notificationType === 'string' ? params.notificationType : undefined,
      createTimeStart: params.startDate
        ? Math.floor(dayjs(params.startDate).startOf('day').valueOf() / 1000)
        : undefined,
      createTimeEnd: params.endDate
        ? Math.floor(dayjs(params.endDate).endOf('day').valueOf() / 1000)
        : undefined,
      currentPage: params.currentPage || 1,
      env: params.env,
      pageSize: params.pageSize || 20,
    };

    const response = await getAppleSubscribeQuery(appleParams);
    
    // 检查响应状态
    if (response.status !== 200 || !response.data) {
      return {
        records: [],
        total: 0,
        currentPage: params.currentPage || 1,
        pageSize: params.pageSize || 20,
      };
    }
    
    // 转换数据格式
    const records: SubscriptionRecord[] = (response.data.records || []).map(
      convertAppleRecordToSubscriptionRecord
    );

    return {
      records,
      total: response.data.total || 0,
      currentPage: response.data.currentPage || params.currentPage || 1,
      pageSize: response.data.pageSize || params.pageSize || 20,
    };
  }

  // 安卓平台调用安卓订阅查询接口（订阅生效时间：当天 00:00:00 ~ 23:59:59，按 epoch 毫秒传）
  if (params.platform === Platform.ANDROID) {
    const googleParams: GoogleSubscribeQueryParams = {
      suppId: params.suppId ? Number(params.suppId) : undefined,
      orderId: params.transactionId,
      createTimeStart: params.startDate
        ? dayjs(params.startDate).startOf('day').valueOf()
        : undefined,
      createTimeEnd: params.endDate
        ? dayjs(params.endDate).endOf('day').valueOf()
        : undefined,
      env: params.env,
      status: params.orderStatus,
      renewalStatus: params.renewalStatus,
      // 安卓通知类型是数字枚举（1-13）
      notificationType:
        typeof params.notificationType === 'number'
          ? params.notificationType
          : typeof params.notificationType === 'string' && params.notificationType !== ''
            ? Number(params.notificationType)
            : undefined,
      currentPage: params.currentPage || 1,
      pageSize: params.pageSize || 20,
    };

    const response = await getGoogleSubscribeQuery(googleParams);

    if (response.status !== 200 || !response.data) {
      return {
        records: [],
        total: 0,
        currentPage: params.currentPage || 1,
        pageSize: params.pageSize || 20,
      };
    }

    const records: SubscriptionRecord[] = (response.data.records || []).map(
      convertGoogleRecordToSubscriptionRecord
    );

    return {
      records,
      total: response.data.total || 0,
      currentPage: response.data.currentPage || params.currentPage || 1,
      pageSize: response.data.pageSize || params.pageSize || 20,
    };
  }

  // 如果既不是苹果也不是安卓，返回空数据
  return {
    records: [],
    total: 0,
    currentPage: params.currentPage || 1,
    pageSize: params.pageSize || 20,
  };
};
